import { useQuery } from "@tanstack/react-query";
import { ArrowRight, ChevronDown, Star } from "lucide-react";
import { Link, useOutletContext } from "react-router-dom";
import { DimensionLine } from "../components/brand";
import { ProductCard } from "../components/ProductCard";
import type { StorefrontContext } from "../components/PublicLayout";
import { Seo } from "../components/Seo";
import { api } from "../lib/api";
import { displayName, shortMoney } from "../lib/format";
import type { ProductSummary } from "../lib/types";

type Content = {
  settings: Record<string, any>;
  faqs: Array<{ publicId: string; question: string; answer: string }>;
  testimonials: Array<{ publicId: string; customerName: string; quote: string; rating: number }>;
};
type Category = { publicId: string; name: string; slug: string; description: string | null };
type ProductList = { items: ProductSummary[]; total: number };

const drawerColumns: Record<number, string> = { 2: "sm:grid-cols-2", 3: "sm:grid-cols-3", 4: "sm:grid-cols-4" };

export function LandingPage() {
  const { openHelp } = useOutletContext<StorefrontContext>();
  const featured = useQuery({ queryKey: ["featured", "shelf"], queryFn: () => api<ProductList>("/products?featured=true&limit=3") });
  const latest = useQuery({ queryKey: ["products", "home-newest"], queryFn: () => api<ProductList>("/products?sort=newest&limit=48") });
  const categories = useQuery({ queryKey: ["categories"], queryFn: () => api<Category[]>("/categories") });
  const content = useQuery({ queryKey: ["content"], queryFn: () => api<Content>("/content") });
  const settings = content.data?.settings;
  const tagline: string = settings?.brand?.tagline ?? "Tiny things, made with love.";
  const lede: string = settings?.hero?.heading ?? "Handcrafted miniatures and 3D-printed pieces, made to bring your tiny worlds to life.";

  const shelf = featured.data?.items.length ? featured.data.items : latest.data?.items.slice(0, 3);
  const allItems = latest.data?.items ?? [];
  const counts = allItems.reduce<Record<string, number>>((map, item) => ({ ...map, [item.category.slug]: (map[item.category.slug] ?? 0) + 1 }), {});
  const drawers = (categories.data ?? []).filter((category) => counts[category.slug]);
  const testimonials = (content.data?.testimonials ?? []).filter((item) => item.customerName !== "Sample customer");
  const shelfIds = new Set(shelf?.map((item) => item.publicId));
  const notePhoto = allItems.find((item) => !shelfIds.has(item.publicId)) ?? allItems[0];

  return (
    <>
      <Seo title="Minis by Cass — tiny things, made with love" fullTitle path="/" />

      {/* Hero: the shelf */}
      <section className="container-page grid gap-x-12 gap-y-8 pb-16 pt-8 md:pt-12 lg:grid-cols-12 lg:pb-24 lg:pt-16">
        <h1 className="font-display text-[3.25rem] leading-[.95] tracking-[-.015em] text-cocoa sm:text-[4.5rem] lg:col-span-5 lg:row-start-1 lg:self-end lg:text-[4.25rem] xl:text-[5rem]">
          {tagline}
        </h1>
        <div className="lg:col-span-7 lg:col-start-6 lg:row-span-2 lg:row-start-1">
          <HeroShelf items={shelf} loading={featured.isLoading || (!featured.data?.items.length && latest.isLoading)} label={settings?.hero?.eyebrow ?? "Made for tiny worlds"} total={latest.data?.total} />
        </div>
        <div className="lg:col-span-5 lg:row-start-2">
          <p className="max-w-[40ch] text-[17px] leading-relaxed text-muted md:text-lg">{lede}</p>
          <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-4">
            <Link to="/shop" className="btn-primary px-7">Browse the shop <ArrowRight size={18} /></Link>
            <Link to="/track" className="link-quiet">Track an order</Link>
          </div>
        </div>
      </section>

      {/* Category cabinet */}
      {drawers.length > 1 && <section className="container-page pb-20 md:pb-28" aria-labelledby="drawers-title">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <h2 id="drawers-title" className="subheading">What are you collecting?</h2>
        </div>
        <div className={`mt-8 grid gap-[6px] rounded-[8px] bg-line p-[6px] ${drawers.length % 2 ? "grid-cols-2 [&>*:last-child]:col-span-2 sm:[&>*:last-child]:col-span-1" : "grid-cols-2"} ${drawerColumns[Math.min(drawers.length, 4)]}`}>
          {drawers.map((category) => {
            const inside = allItems.filter((item) => item.category.slug === category.slug).slice(0, 3);
            return <Link
              key={category.publicId}
              to={`/shop?category=${category.slug}`}
              className="group relative flex flex-col overflow-hidden rounded-[4px] bg-white transition duration-300 ease-settle hover:shadow-lift"
            >
              <span className="relative flex h-10 items-start justify-center gap-2 overflow-hidden bg-[#F3E3E4] px-4 pt-2.5 shadow-[inset_0_8px_10px_-8px_rgba(69,35,41,.35)] transition-[height] duration-300 ease-settle group-hover:h-[4.5rem] md:h-12 md:group-hover:h-20" aria-hidden="true">
                {inside.map((item) => <img key={item.publicId} src={item.imageUrl} alt="" loading="lazy" className="size-14 rounded-[4px] object-cover shadow-sticker md:size-16" />)}
              </span>
              <span className="flex flex-1 flex-col items-center justify-center gap-2.5 border-t border-cocoa/10 px-3 py-5 text-center shadow-[inset_0_-3px_0_rgba(69,35,41,.06)] md:py-7">
                <span className="rounded-[3px] border-[1.5px] border-cocoa/25 bg-frosting/50 px-3 py-1 font-hand text-xl leading-tight text-cocoa transition-colors group-hover:border-ribbon md:text-2xl">{displayName(category.name)}</span>
                <span className="size-3 rounded-full bg-cocoa/70 shadow-[0_2px_0_rgba(69,35,41,.25)] transition group-hover:bg-ribbon" aria-hidden="true" />
                <span className="text-[13px] text-muted">{counts[category.slug] >= 48 ? "48+" : counts[category.slug]} piece{counts[category.slug] === 1 ? "" : "s"}</span>
              </span>
            </Link>;
          })}
        </div>
      </section>}

      {/* Fresh on the shelf */}
      <section className="container-page pb-20 md:pb-28" aria-labelledby="fresh-title">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <h2 id="fresh-title" className="subheading">Fresh on the shelf</h2>
          {latest.data && <Link to="/shop" className="link-quiet">See all {latest.data.total} <ArrowRight size={16} /></Link>}
        </div>
        {latest.isError && <p className="mt-6 text-muted">The shelf didn’t load. Refresh the page to try again.</p>}
        <div className="mt-9 grid grid-cols-2 gap-x-4 gap-y-10 sm:gap-x-6 lg:grid-cols-4 lg:gap-x-8">
          {latest.isLoading && [0, 1, 2, 3, 4].map((n) => <div key={n} className={`aspect-square animate-pulse rounded-[6px] bg-frosting ${n === 0 ? "col-span-2 lg:row-span-2" : ""}`} />)}
          {allItems.slice(0, 5).map((product, index) => <div key={product.publicId} className={index === 0 && allItems.length >= 5 ? "col-span-2 lg:row-span-2" : ""}>
            <ProductCard product={product} feature={index === 0 && allItems.length >= 5} />
          </div>)}
        </div>
      </section>

      {/* A note from Cass + how ordering works */}
      <section className="bg-frosting py-20 md:py-28" aria-labelledby="note-title">
        <div className="container-page">
          <div className="grid items-center gap-12 md:grid-cols-12">
            {notePhoto && <div className="relative mx-auto w-[78%] max-w-sm md:col-span-5 md:w-full">
              <div className="rotate-[-2.5deg] bg-white p-3 pb-12 shadow-soft">
                <img src={notePhoto.imageUrl} alt={notePhoto.name} loading="lazy" className="aspect-square w-full object-cover" />
                <p className="absolute bottom-3 left-4 right-4 truncate font-hand text-xl text-cocoa">{notePhoto.name}</p>
              </div>
              <span className="absolute -top-3 left-1/2 h-7 w-24 -translate-x-1/2 rotate-[4deg] bg-butter/80" aria-hidden="true" />
            </div>}
            <div className={notePhoto ? "md:col-span-6 md:col-start-7" : "md:col-span-8"}>
              <CassNote id="note-title" text={settings?.hero?.description} />
            </div>
          </div>

          <div className="mt-20 md:mt-28">
            <h2 className="subheading">How ordering works</h2>
            <OrderingRuler />
          </div>
        </div>
      </section>

      {/* Kind words + questions */}
      <section className="container-page grid gap-16 pt-20 md:pt-28 lg:grid-cols-12 lg:gap-12">
        {testimonials.length > 0 && <div className="lg:col-span-6">
          <h2 className="subheading">Kind words</h2>
          <div className="mt-10 grid gap-12">
            {testimonials.slice(0, 3).map((item, index) => <figure key={item.publicId} className={index % 2 ? "sm:ml-[12%]" : ""}>
              <span className="flex gap-0.5 text-ribbon" aria-label={`${item.rating} out of 5 stars`}>{Array.from({ length: item.rating }, (_, n) => <Star key={n} size={15} className="fill-current" />)}</span>
              <blockquote className={`mt-3 text-cocoa ${index === 0 ? "font-display text-[1.75rem] leading-[1.2] md:text-[2.1rem]" : "text-xl leading-relaxed"}`}>“{item.quote}”</blockquote>
              <figcaption className="mt-3 font-hand text-2xl text-ribbon-ink">{item.customerName}</figcaption>
            </figure>)}
          </div>
        </div>}
        <div className={testimonials.length > 0 ? "lg:col-span-5 lg:col-start-8" : "lg:col-span-8"}>
          <h2 className="subheading">Before you order</h2>
          <div className="mt-8 divide-y divide-line border-y border-line">
            {content.data?.faqs.map((faq) => <details key={faq.publicId} className="group">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 py-5 text-[17px] font-bold text-cocoa transition-colors hover:text-ribbon-ink [&::-webkit-details-marker]:hidden">
                {faq.question}<ChevronDown size={19} className="shrink-0 text-muted transition-transform duration-300 group-open:rotate-180" />
              </summary>
              <p className="-mt-1 pb-6 pr-8 leading-relaxed text-muted">{faq.answer}</p>
            </details>)}
          </div>
          <p className="mt-6 text-muted">Something else on your mind? <button type="button" onClick={openHelp} className="link-quiet">Ask Cass</button></p>
        </div>
      </section>
    </>
  );
}

/**
 * Three featured pieces displayed in a pink cubby shelf (the kind Cass prints for collectors),
 * with a drawer underneath that opens the shop. This is the page's one big moment.
 */
function HeroShelf({ items, loading, label, total }: { items?: ProductSummary[]; loading: boolean; label: string; total?: number }) {
  const slots = loading ? [undefined, undefined, undefined] : (items ?? []).slice(0, 3);
  const areas = ["col-span-2 row-span-2", "col-start-3 row-start-1", "col-start-3 row-start-2"];
  const tilts = [-5, 4, -3];
  return (
    <div className="relative">
      <DimensionLine label={label} className="mb-3 hidden sm:flex" />
      <div className="rounded-[6px] bg-ribbon p-[10px] shadow-soft md:p-3">
        <div className="grid grid-cols-3 grid-rows-2 gap-[10px] md:gap-3">
          {slots.map((item, index) => <div key={item?.publicId ?? index} className={`relative aspect-square overflow-visible rounded-[2px] bg-frosting ${areas[index]}`}>
            {!item && <div className="h-full w-full animate-pulse bg-frosting" />}
            {item && <Link
              to={`/products/${item.slug}`}
              aria-label={`${item.name}, ${shortMoney(item.basePriceCentavos)}`}
              className="place-in group absolute inset-0 overflow-hidden rounded-[2px]"
              style={{ animationDelay: `${120 + index * 110}ms` }}
            >
              <img src={item.imageUrl} alt="" className="h-full w-full object-cover transition duration-700 ease-settle group-hover:scale-[1.04]" />
              <span className="pointer-events-none absolute inset-0 shadow-[inset_0_14px_16px_-12px_rgba(69,35,41,.45)]" aria-hidden="true" />
              <span className="absolute inset-x-0 bottom-0 hidden translate-y-full bg-white/95 px-3 py-2 text-[13px] font-bold leading-snug text-cocoa transition-transform duration-300 ease-settle group-hover:translate-y-0 group-focus-visible:translate-y-0 md:block">{item.name}</span>
            </Link>}
            {item && <span
              className="tag-swing pointer-events-none absolute right-2 top-0 z-10 flex flex-col items-center md:right-4"
              style={{ ["--tilt" as string]: `${tilts[index]}deg`, animationDelay: `${520 + index * 120}ms` }}
              aria-hidden="true"
            >
              <span className="h-3 w-px bg-cocoa/50 md:h-5" />
              <span className={`relative rounded-[3px] bg-butter px-2 py-1 font-display leading-none text-cocoa shadow-sticker before:absolute before:left-1/2 before:top-1 before:size-1 before:-translate-x-1/2 before:rounded-full before:bg-cocoa/40 ${index === 0 ? "pt-3 text-lg md:text-xl" : "pt-2.5 text-sm md:text-base"}`}>{shortMoney(item.basePriceCentavos)}</span>
            </span>}
          </div>)}
        </div>
        <Link
          to="/shop"
          className="place-in group mt-[10px] flex flex-col items-center justify-center gap-2.5 rounded-[2px] bg-white px-4 py-4 shadow-[inset_0_-3px_0_rgba(69,35,41,.07)] transition duration-300 ease-settle hover:translate-y-[3px] md:mt-3 md:py-5"
          style={{ animationDelay: "460ms" }}
        >
          <span className="rounded-[3px] border-[1.5px] border-cocoa/25 px-3 py-0.5 font-hand text-xl leading-tight text-cocoa transition-colors group-hover:border-ribbon">
            {total ? `open the drawer, all ${total} pieces` : "open the drawer"}
          </span>
          <span className="size-3 rounded-full bg-cocoa/70 shadow-[0_2px_0_rgba(69,35,41,.25)] transition group-hover:bg-ribbon" aria-hidden="true" />
        </Link>
      </div>
    </div>
  );
}

/** Cass's own welcome text. If it opens with a short greeting, that greeting becomes the heading. */
function CassNote({ id, text }: { id: string; text?: string }) {
  const body = text ?? "Every piece here is made by hand or printed to order, in small batches, for collectors and miniature lovers.";
  const match = body.match(/^(.{4,40}?[!.])\s+(.*)$/s);
  const heading = match ? match[1] : "A note from Cass";
  const rest = match ? match[2] : body;
  return (
    <div>
      <h2 id={id} className="subheading">{heading}</h2>
      <p className="mt-6 max-w-[56ch] text-lg leading-[1.75] text-cocoa/80">{rest}</p>
      <p className="mt-6 font-hand text-3xl leading-none text-ribbon-ink">— Cass</p>
    </div>
  );
}

const orderingSteps = [
  ["Choose and personalize", "Pick a piece, then its style, colors, and the little details that make it yours."],
  ["Add reference photos", "For custom pieces, attach clear photos so Cass can see exactly what you have in mind."],
  ["Pay, then send your receipt", "Pay with the QR shown after checkout and upload your receipt. Making starts once it’s verified."],
  ["Track it to your shelf", "Follow your order from the making queue to pickup or delivery with your order number."]
] as const;

/** The four ordering steps marked off along a ruler: a real sequence, so it earns its numbers. */
function OrderingRuler() {
  return (
    <ol className="relative mt-10 grid gap-10 pl-8 md:grid-cols-4 md:gap-8 md:pl-0 md:pt-10">
      <span className="ruler-v absolute bottom-0 left-0 top-0 md:hidden" aria-hidden="true" />
      <span className="ruler absolute left-0 right-0 top-0 hidden md:block" aria-hidden="true" />
      {orderingSteps.map(([title, text], index) => <li key={title} className="relative">
        <span className="absolute -left-8 top-2 h-px w-5 bg-ribbon md:-top-10 md:left-0 md:h-6 md:w-px" aria-hidden="true" />
        <span className="font-display text-4xl leading-none text-ribbon">{index + 1}</span>
        <h3 className="mt-3 text-lg font-bold text-cocoa">{title}</h3>
        <p className="mt-2 max-w-[32ch] leading-relaxed text-muted">{text}</p>
      </li>)}
    </ol>
  );
}
